import { Zap, ArrowRight } from 'lucide-react';
import { useLocation } from 'wouter';

import type { RecommendationsCardProps } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { ChatCard, GhostLinkButton, mix } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { SkBar, SkChip, SkBtn, SkCircle } from './skeletons';

type Priority = 'high' | 'med' | 'low';

const PRIORITY: Record<Priority, { color: string; label: string }> = {
  high: { color: 'var(--color-orange-500)', label: 'Urgent' },
  med: { color: 'var(--color-purple-400)', label: 'Recommended' },
  low: { color: 'var(--color-blue-400)', label: 'When you can' },
};

const ORANGE = 'var(--color-orange-500)';

export function RecommendationsCardWidget({
  recs,
  total,
  navTo = '/recommendations',
}: RecommendationsCardProps) {
  const [, setLocation] = useLocation();
  const shown = recs.slice(0, 3);
  const count = total ?? recs.length;
  const more = count - shown.length;
  const showViewAll = more > 0;
  const go = () => setLocation(navTo);

  return (
    <ChatCard className="flex flex-col gap-[13px] p-[18px] text-sm">
      <div className="flex items-center gap-2.5">
        <span
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: mix(ORANGE, 14), border: `1px solid ${mix(ORANGE, 28)}`, color: ORANGE }}
        >
          <Zap className="size-[14px]" aria-hidden />
        </span>
        <span className="text-[15px] font-bold text-foreground">Recommendations</span>
        <span
          className="font-mono text-[10.5px] uppercase tracking-[0.05em]"
          style={{ color: 'var(--foreground-subtle)' }}
        >
          {count} open
        </span>
        {showViewAll && (
          <GhostLinkButton className="ml-auto" data-nav-to="recommendations" onClick={go}>
            View all <ArrowRight className="size-3" aria-hidden />
          </GhostLinkButton>
        )}
      </div>

      <div className="flex flex-col gap-2.5">
        {shown.map((rec) => {
          const p = PRIORITY[rec.priority];
          return (
            <div
              key={rec.id}
              className="flex flex-col gap-[9px] rounded-xl border border-border bg-secondary p-[13px]"
              style={{ borderLeft: `3px solid ${p.color}` }}
            >
              <div className="flex items-start gap-2">
                <span className="min-w-0 flex-1 text-[13px] font-bold leading-[1.3] text-foreground">
                  {capitalizeFirst(rec.title)}
                </span>
                <span
                  className="shrink-0 rounded-full px-[7px] py-[2px] font-mono text-[9px] font-bold uppercase tracking-[0.05em]"
                  style={{ color: p.color, background: mix(p.color, 13), border: `1px solid ${mix(p.color, 28)}` }}
                >
                  {p.label}
                </span>
              </div>

              {rec.why && (
                <div className="flex items-baseline gap-[7px]">
                  <span
                    className="shrink-0 font-mono text-[9.5px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: 'var(--foreground-subtle)' }}
                  >
                    Why
                  </span>
                  <span className="text-[11.5px] leading-[1.5] text-muted-foreground text-pretty">
                    {capitalizeFirst(rec.why)}
                  </span>
                </div>
              )}

              {rec.steps && rec.steps.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {rec.steps.map((step, j) => (
                    <RecStepButton key={step.action} primary={j === 0}>
                      {capitalizeFirst(step.label)}
                    </RecStepButton>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ChatCard>
  );
}

/** Compact primary/ghost step button (lcPrimaryBtn / lcGhostBtn, scaled). */
function RecStepButton({ primary, children }: { primary: boolean; children: React.ReactNode }) {
  if (primary) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-[11px] px-[13px] py-[7px] text-[12px] font-semibold text-white"
        style={{ background: 'var(--gradient-primary)', boxShadow: '0 6px 18px rgba(124,58,237,0.32)' }}
      >
        {children}
      </button>
    );
  }
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1.5 rounded-[11px] border border-border bg-secondary px-[13px] py-[7px] text-[12px] font-semibold text-foreground transition-colors hover:border-[var(--border-strong)]"
    >
      {children}
    </button>
  );
}

export function RecommendationsCardSkeleton({ className, items = 3 }: { className?: string; items?: number }) {
  return (
    <ChatCard className={cn('flex flex-col gap-[13px] p-[18px]', className)}>
      <div className="flex items-center gap-2.5">
        <SkCircle size="28px" radius="8px" />
        <SkBar width="130px" height="13px" />
        <span className="ml-auto">
          <SkBtn width="84px" />
        </span>
      </div>
      {Array.from({ length: items }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col gap-[9px] rounded-xl border border-border bg-secondary p-[13px]"
          style={{ borderLeft: '3px solid var(--border-strong)' }}
        >
          <div className="flex gap-2">
            <SkBar width="62%" height="11px" />
            <span className="ml-auto">
              <SkChip width="64px" height="16px" />
            </span>
          </div>
          <SkBar width="90%" height="8px" />
          <div className="flex gap-2">
            <SkBtn width="120px" />
            <SkBtn width="96px" />
          </div>
        </div>
      ))}
    </ChatCard>
  );
}
